'use client';

import React from 'react';
import { motion, useInView } from 'framer-motion';
import Header from '@/components/common/Header';
import Footer from '../components/Footer';
import Icon from '@/components/ui/AppIcon';
import type { Metadata } from 'next';

const locations = [
  {
    id: 'sarthana',
    name: 'Sarthana Center',
    address: 'Sarthana Jakatnaka, Surat, Gujarat 395006',
    phone: '+91 261 XXX XXXX',
    email: 'sarthana@sevasetu.org',
    hours: 'Mon-Sat: 9:00 AM - 6:00 PM',
    services: ['Health Checkups', 'Medical Consultations', 'Health Camps', 'Wellness Programs'],
    icon: 'BuildingOfficeIcon',
    color: 'primary',
    mapLink: 'https://maps.google.com',
  },
  {
    id: 'varachha',
    name: 'Varachha Center',
    address: 'Varachha Road, Surat, Gujarat 395006',
    phone: '+91 261 XXX XXXX',
    email: 'varachha@sevasetu.org',
    hours: 'Mon-Sat: 9:00 AM - 6:00 PM',
    services: ['Community Support', 'Food Distribution', 'Clothing Assistance', 'Education Programs'],
    icon: 'HomeModernIcon',
    color: 'secondary',
    mapLink: 'https://maps.google.com',
  },
  {
    id: 'kadodara',
    name: 'Kadodara Center',
    address: 'Kadodara, Surat, Gujarat 394327',
    phone: '+91 261 XXX XXXX',
    email: 'kadodara@sevasetu.org',
    hours: 'Mon-Sat: 9:00 AM - 6:00 PM',
    services: ['Rural Healthcare', 'Community Outreach', 'Volunteer Programs', 'Health Education'],
    icon: 'MapPinIcon',
    color: 'accent',
    mapLink: 'https://maps.google.com',
  },
];

const LocationsPage = () => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const getColorClasses = (color: string) => {
    const colorMap: Record<string, { bg: string; text: string; icon: string }> = {
      primary: { bg: 'bg-primary/10', text: 'text-primary', icon: 'bg-primary' },
      secondary: { bg: 'bg-secondary/10', text: 'text-secondary', icon: 'bg-secondary' },
      accent: { bg: 'bg-accent/10', text: 'text-accent', icon: 'bg-accent' },
    };
    return colorMap[color] || colorMap.primary;
  };

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background pt-16 lg:pt-20">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary/10 via-secondary/5 to-accent/10 py-10 sm:py-16 lg:py-24">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto text-center"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6"
              >
                <Icon name="MapPinIcon" size={20} />
                <span className="text-sm font-semibold">Find Us</span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-4xl lg:text-5xl font-heading font-bold text-foreground mb-6"
              >
                Our Locations
              </motion.h1>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-lg text-muted-foreground max-w-2xl mx-auto"
              >
                Visit any of our centers across Surat to access our services, volunteer, or learn more about our mission to serve the community.
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* Locations Grid */}
        <section ref={ref} className="py-12 md:py-20 bg-background">
          <div className="container mx-auto px-4">
            <motion.div
              initial="hidden"
              animate={isInView ? 'visible' : 'hidden'}
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.2,
                    delayChildren: 0.1,
                  },
                },
              }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8"
            >
              {locations.map((location) => {
                const colors = getColorClasses(location.color);
                return (
                  <motion.div
                    key={location.id}
                    id={location.id}
                    variants={{
                      hidden: { opacity: 0, y: 50 },
                      visible: { opacity: 1, y: 0 },
                    }}
                    whileHover={{ scale: 1.03, y: -8 }}
                    transition={{ duration: 0.3 }}
                    className="bg-card rounded-2xl overflow-hidden shadow-subtle hover:shadow-elevated transition-all duration-300 scroll-mt-24"
                  >
                    <div className={`${colors.icon} p-8 flex items-center justify-center`}>
                      <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <Icon name={location.icon as any} size={32} className="text-white" />
                      </div>
                    </div>

                    <div className="p-6 space-y-4">
                      <h3 className="text-2xl font-heading font-bold text-foreground">
                        {location.name}
                      </h3>

                      <div className="space-y-3">
                        <div className="flex items-start space-x-3">
                          <Icon name="MapPinIcon" size={20} className={colors.text} />
                          <p className="text-sm text-muted-foreground flex-1">
                            {location.address}
                          </p>
                        </div>

                        <div className="flex items-center space-x-3">
                          <Icon name="PhoneIcon" size={20} className={colors.text} />
                          <a
                            href={`tel:${location.phone}`}
                            className="text-sm text-muted-foreground hover:text-primary transition-colors"
                          >
                            {location.phone}
                          </a>
                        </div>

                        <div className="flex items-center space-x-3">
                          <Icon name="EnvelopeIcon" size={20} className={colors.text} />
                          <a
                            href={`mailto:${location.email}`}
                            className="text-sm text-muted-foreground hover:text-primary transition-colors"
                          >
                            {location.email}
                          </a>
                        </div>

                        <div className="flex items-center space-x-3">
                          <Icon name="ClockIcon" size={20} className={colors.text} />
                          <p className="text-sm text-muted-foreground">
                            {location.hours}
                          </p>
                        </div>
                      </div>

                      <div className={`${colors.bg} rounded-xl p-4 space-y-2`}>
                        <h4 className="text-sm font-heading font-semibold text-foreground">
                          Services Available:
                        </h4>
                        <ul className="space-y-1">
                          {location.services.map((service, index) => (
                            <li key={index} className="flex items-center space-x-2 text-sm text-muted-foreground">
                              <Icon name="CheckCircleIcon" size={16} className={colors.text} />
                              <span>{service}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <motion.a
                        href={location.mapLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        className={`${colors.icon} text-white px-6 py-3 rounded-xl font-heading font-semibold hover:opacity-90 transition-all duration-300 shadow-cta hover:shadow-elevated flex items-center justify-center space-x-2 w-full`}
                      >
                        <Icon name="MapIcon" size={20} />
                        <span>Get Directions</span>
                      </motion.a>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>

        {/* Contact CTA */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="py-20 bg-gradient-to-br from-primary/10 via-secondary/5 to-accent/10"
        >
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center space-y-6">
              <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground">
                Can't Visit in Person?
              </h2>
              <p className="text-lg text-muted-foreground">
                Reach out to us online. We're here to help and answer any questions you may have about our services and programs.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                <motion.a
                  href="tel:+912612345678"
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full sm:w-auto px-8 py-4 rounded-xl font-heading font-semibold text-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 shadow-cta hover:shadow-elevated flex items-center justify-center space-x-2"
                >
                  <Icon name="PhoneIcon" size={24} />
                  <span>Call Us</span>
                </motion.a>
                <motion.a
                  href="mailto:info@sevasetu.org"
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full sm:w-auto px-8 py-4 rounded-xl font-heading font-semibold text-lg bg-secondary text-secondary-foreground hover:bg-secondary/90 transition-all duration-300 shadow-cta hover:shadow-elevated flex items-center justify-center space-x-2"
                >
                  <Icon name="EnvelopeIcon" size={24} />
                  <span>Email Us</span>
                </motion.a>
              </div>
            </div>
          </div>
        </motion.section>

        <Footer />
      </main>
    </>
  );
};

export default LocationsPage;
